import { useCallback, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { Button } from '@/components/ui/Button/Button';
import {
  formatAuthorSheetsOneDecimal,
  isSupportedProjectMetricsFile,
  tryComputeAuthorSheetsAndPagesFromFile,
} from '@/lib/projectFileMetrics/authorSheetsCalculator';
import {
  AuthorSheetsExtractorResultCard,
  type AuthorSheetsExtractorResultRow,
} from './AuthorSheetsExtractorResultCard';
import './AuthorSheetsExtractorMiniApp.css';

const MAX_FILES_PER_BATCH = 10;

type ProgressState = {
  current: number;
  total: number;
  currentFileName: string;
};

function makeId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

const numberFormatter = new Intl.NumberFormat('ru-RU');

function formatCharacters(value: number): string {
  return numberFormatter.format(Math.max(0, Math.round(value)));
}

export function AuthorSheetsExtractorMiniApp() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processingRef = useRef(false);
  const [rows, setRows] = useState<AuthorSheetsExtractorResultRow[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [batchMessage, setBatchMessage] = useState<string | null>(null);
  const busy = progress !== null;

  const appendFiles = useCallback(async (files: File[]) => {
    if (processingRef.current) return;
    const supported = files.filter((f) => isSupportedProjectMetricsFile(f));
    const list = supported.slice(0, MAX_FILES_PER_BATCH);
    const unsupportedCount = files.length - supported.length;
    const skippedByLimitCount = Math.max(0, files.length - unsupportedCount - list.length);
    if (unsupportedCount > 0 || skippedByLimitCount > 0) {
      const parts: string[] = [];
      if (unsupportedCount > 0) parts.push(`пропущено форматов: ${unsupportedCount}`);
      if (skippedByLimitCount > 0) parts.push(`лимит за запуск: ${MAX_FILES_PER_BATCH}`);
      setBatchMessage(parts.join(' · '));
    } else {
      setBatchMessage(null);
    }
    if (list.length === 0) return;
    processingRef.current = true;
    try {
      const next: AuthorSheetsExtractorResultRow[] = [];
      for (let i = 0; i < list.length; i += 1) {
        const file = list[i];
        setProgress({ current: i + 1, total: list.length, currentFileName: file.name });
        const r = await tryComputeAuthorSheetsAndPagesFromFile(file);
        if (!r.ok) {
          next.push({
            id: makeId(),
            fileName: file.name,
            authorSheetsDisplay: 'N/A',
            charactersDisplay: 'N/A',
            pagesDisplay: 'N/A',
            errorMessage: r.errorMessage,
          });
        } else {
          const al = formatAuthorSheetsOneDecimal(r.metrics.authorSheets);
          const pages = String(Math.max(0, Math.round(r.metrics.pages)));
          next.push({
            id: makeId(),
            fileName: file.name,
            authorSheetsDisplay: al,
            charactersDisplay: formatCharacters(r.metrics.characters),
            pagesDisplay: pages,
          });
        }
      }
      if (next.length) setRows((prev) => [...next, ...prev]);
    } finally {
      setProgress(null);
      processingRef.current = false;
    }
  }, []);

  const handlePickClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await appendFiles(picked);
    },
    [appendFiles]
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  /** Не сбрасывать highlight при переходе с паддинга на дочерний текст (иначе дёрганье). */
  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const rel = e.relatedTarget as Node | null;
    if (rel && e.currentTarget.contains(rel)) return;
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      await appendFiles(Array.from(e.dataTransfer.files));
    },
    [appendFiles]
  );

  const handleRemove = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const handleClear = useCallback(() => {
    setRows([]);
  }, []);

  return (
    <div className="author-sheets-mini-app" aria-busy={busy}>
      <p className="author-sheets-mini-app__intro">
        Добавьте PDF или DOCX — мини-апп покажет символы, страницы и авторские листы по каждому файлу.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        multiple
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />

      <div
        role="button"
        tabIndex={0}
        className={`author-sheets-mini-app__drop${dragActive ? ' author-sheets-mini-app__drop_drag' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (!busy) handlePickClick();
        }}
        onKeyDown={(e) => {
          if (busy) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handlePickClick();
          }
        }}
      >
        <span>Перетащите файлы сюда или нажмите для выбора</span>
        <span className="author-sheets-mini-app__hint">
          Поддерживаются PDF и DOCX. Не более {MAX_FILES_PER_BATCH} файлов за раз.
        </span>
      </div>

      {rows.length > 0 ? (
        <div className="author-sheets-mini-app__actions">
          <Button type="button" variant="outlined" size="s" fullWidth onClick={handleClear} disabled={busy}>
            Очистить список
          </Button>
        </div>
      ) : null}

      {progress ? (
        <section className="author-sheets-mini-app__progress" role="status" aria-live="polite">
          <div className="author-sheets-mini-app__progress-head">
            <span className="author-sheets-mini-app__progress-title">
              Обработка {progress.current} из {progress.total}
            </span>
            <span className="author-sheets-mini-app__progress-percent">
              {Math.round((progress.current / progress.total) * 100)}%
            </span>
          </div>
          <span className="author-sheets-mini-app__progress-file" title={progress.currentFileName}>
            {progress.currentFileName}
          </span>
          <div
            className="author-sheets-mini-app__progress-track"
            role="progressbar"
            aria-label="Прогресс обработки файлов"
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.current}
          >
            <span
              className="author-sheets-mini-app__progress-fill"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
        </section>
      ) : null}

      {batchMessage ? <p className="author-sheets-mini-app__batch-message">{batchMessage}</p> : null}

      {rows.length > 0 ? (
        <ul className="author-sheets-mini-app__list" aria-label="Список обработанных файлов">
          {rows.map((row) => {
            return (
              <AuthorSheetsExtractorResultCard key={row.id} row={row} busy={busy} onRemove={handleRemove} />
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
